import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { MagnifyingGlass, X } from "@phosphor-icons/react";

/**
 * Searchable santri picker.
 * Value = { nis, nama, kamar_nama, kelas_nama } | null. onChange(newValue).
 * Cari via nama, dengan filter opsional per kamar/kelas/halaqah.
 */
export default function SantriSelect({ value, onChange, placeholder = "Cari nama santri…", testid = "santri-select" }) {
  const [q, setQ] = useState("");
  const [kamar, setKamar] = useState("");
  const [kamarList, setKamarList] = useState([]);
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    api.get("/master/kamar").then((r) => setKamarList(r.data)).catch(() => {});
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      if (!open) return;
      const params = { limit: 30 };
      if (q) params.q = q;
      if (kamar) params.kamar_id = kamar;
      try {
        const { data } = await api.get("/santri", { params });
        if (!cancelled) setResults(data);
      } catch (_) { if (!cancelled) setResults([]); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, kamar, open]);

  const select = (s) => {
    onChange && onChange({ nis: s.nis, nama: s.nama, kamar_nama: s.kamar_nama, kelas_nama: s.kelas_nama, id: s.id });
    setOpen(false);
    setQ("");
  };
  const clear = () => onChange && onChange(null);

  return (
    <div className="relative" ref={boxRef}>
      {value ? (
        <div className="imsyaf-input flex items-center justify-between gap-2 bg-green-50 border-green-200" data-testid={`${testid}-value`}>
          <div className="min-w-0">
            <div className="font-medium text-[#064E3B] truncate">{value.nama}</div>
            <div className="text-[11px] text-slate-500 truncate">
              NIS: <span className="font-mono">{value.nis}</span>
              {value.kamar_nama && <> · Kamar: <b>{value.kamar_nama}</b></>}
              {value.kelas_nama && <> · Kelas: <b>{value.kelas_nama}</b></>}
            </div>
          </div>
          <button type="button" onClick={clear} className="text-slate-500 hover:text-red-600" data-testid={`${testid}-clear`}>
            <X size={16} />
          </button>
        </div>
      ) : (
        <>
          <div className="relative">
            <MagnifyingGlass className="absolute left-3 top-2.5 text-slate-400" size={16} />
            <input
              className="imsyaf-input pl-9"
              placeholder={placeholder}
              value={q}
              onFocus={() => setOpen(true)}
              onChange={(e) => { setQ(e.target.value); setOpen(true); }}
              data-testid={testid}
            />
          </div>
          {open && (
            <div className="absolute z-30 left-0 right-0 mt-1 imsyaf-card bg-white shadow-lg max-h-80 overflow-hidden">
              <div className="p-2 border-b border-slate-100 flex gap-2 items-center bg-slate-50">
                <label className="text-[11px] text-slate-500">Filter kamar:</label>
                <select className="imsyaf-input !py-1 text-xs" value={kamar} onChange={(e) => setKamar(e.target.value)}>
                  <option value="">Semua kamar</option>
                  {kamarList.map((k) => <option key={k.id} value={k.id}>{k.nama}</option>)}
                </select>
              </div>
              <div className="max-h-64 overflow-y-auto">
                {results.length === 0 && (
                  <div className="p-4 text-center text-xs text-slate-400">Ketik nama santri atau pilih kamar…</div>
                )}
                {results.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => select(s)}
                    className="w-full text-left px-3 py-2 hover:bg-green-50 transition-colors border-b border-slate-50"
                    data-testid={`${testid}-option-${s.nis}`}
                  >
                    <div className="font-medium text-[#064E3B] text-sm">{s.nama}</div>
                    <div className="text-[11px] text-slate-500">
                      <span className="font-mono">{s.nis}</span>
                      {s.kamar_nama && <> · <b>{s.kamar_nama}</b></>}
                      {s.kelas_nama && <> · {s.kelas_nama}</>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
